import React, { useState, useEffect } from "react";
import { Grid as GridLoading } from "react-loader-spinner";
import html2canvas from "html2canvas";
import SlotCounter from "react-slot-counter";
import { TypeAnimation } from "react-type-animation";
import { Tooltip as ReactTooltip } from "react-tooltip";

// Import styles
import "../assets/styles/exec/Executive.scss";
import { styled } from "@mui/material/styles";

// Import modular components
import ExecChart from "./exec/ExecChart";
import FadeIn from "./compliance/FadeIn";
import NewChatInterface from "./exec/NewChatInterface";
import Product from "./exec/Product";

// Import MUI elements
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Skeleton from "@mui/material/Skeleton";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";

// Import framework to manage datepicker
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Import icons
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SendIcon from "@mui/icons-material/Send";

const Item = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  textAlign: "left",
}));

const ModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 850,
  height: 600,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
};

const ImageStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxHeight: 800,
  bgcolor: "background.paper",
  border: "1px solid #000",
  p: 4,
  overflow: "scroll",
  display: "block",
  padding: 0,
};

function parsePercentageString(percentageString: string) {
  const regex = /(\d+(\.\d+)?)/;

  const matches = percentageString.match(regex);

  if (matches && matches.length > 1) {
    return Math.round(parseFloat(matches[1]) * 10) / 10;
  } else {
    return NaN;
  }
}

function Executive({ parentToChild }: { parentToChild: any }) {
  const { mode } = parentToChild;

  // Radio button choice
  // Default value is Total Call Volume
  const [selectedRadio, setSelectedRadio] = useState("resolution_rate");
  const [selectedCallCenter, setSelectedCallCenter] = useState("All");

  const [totalCallData, setTotalCall] = useState<any>([]);
  const [aveHandleData, setAveHandleTime] = useState<any>([]);
  const [resolRateData, setResolRate] = useState<any>([]);
  const [aveWaitData, setAveWaittime] = useState<any>([]);

  const [numbersAsInt, setNumbersAsInt] = useState(0);

  // State variables for alerts
  const [alertData, setAlertData] = useState<any>([]);
  const [alertText, setAlertText] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);

  // State variables for highlights
  const [highlightData, setHighlightData] = useState<any>([]);
  const [highlightText, setHighlightText] = useState<any>([]);
  const [isHighlightLoading, setIsHighlightLoading] = useState(true);

  // Add state for selected option and start date
  const [selectedOption, setSelectedOption] = useState("Last 7 days");
  const [endDate, setEndDate] = useState(dayjs("2024-01-31"));
  const [startDate, setStartDate] = useState(dayjs("2024-01-25"));

  // Variables for sending a report
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [email, setEmail] = useState("");
  const [comments, setComments] = useState("");
  const [frequency, setFrequency] = useState("Daily");
  const [recurrences, setRecurrences] = useState("1");
  const [rangeStartDate, setRangeStartDate] = useState(dayjs());
  const [rangeEndDate, setRangeEndDate] = useState(dayjs());
  const Today = dayjs();

  useEffect(() => {
    if (
      rangeStartDate &&
      rangeEndDate &&
      rangeStartDate.isAfter(rangeEndDate, "day")
    ) {
      setRangeStartDate(rangeEndDate);
    }
  }, [rangeStartDate, rangeEndDate]);

  const [isImageOpen, setIsImageOpen] = useState<boolean>(false);

  // This state is passed to child components when everything is being reset
  const [isBeingResetProduct, setIsBeingResetProduct] =
    useState<boolean>(false);
  const [isBeingResetChart, setIsBeingResetChart] = useState<boolean>(false);

  // Set screenshot data for send report button
  const [screenshot, setScreenshot] = useState<any>();

  const onButtonClick = async () => {
    const canvas = await html2canvas(document.body);
    const dataUrl = canvas.toDataURL("image/png");

    setScreenshot(dataUrl);
  };

  // Get useState for product selection
  const [selectedProductFromChild, setSelectedProductFromChild] = useState("");
  const handleSelectedProductChange = (label: string) => {
    setSelectedProductFromChild(label);
  };

  // Get useState for callreason selection
  const [selectedReasonFromChild, setSelectedReasonFromChild] = useState("");
  const handleSelectedReasonChange = (label: string) => {
    setSelectedReasonFromChild(label);
  };

  // Handle click for alerts
  const [clickedLabel, setClickedLabel] = useState<number | null>(null);
  const handleLabelClick = (labelIndex: number) => {
    if (labelIndex === 0) {
      setSelectedCallCenter(alertData.key_alerts[0].cc_id);
      setSelectedRadio(alertData.key_alerts[0].key_metric[0]);
    } else if (labelIndex === 1) {
      setSelectedCallCenter(alertData.key_alerts[1].cc_id);
      setSelectedRadio(alertData.key_alerts[1].key_metric[0]);
    }
    setSelectedProductFromChild("");
    setSelectedReasonFromChild("");
    setIsBeingResetProduct(true);
    setIsBeingResetChart(true);
    setClickedLabel(labelIndex);
  };

  // Updated DatePicker onChange handler
  const handleEndDateChange = (
    selectedOption: string,
    newDate: dayjs.Dayjs
  ) => {
    setClickedLabel(null);

    // Calculate updatedStartDate asynchronously
    setEndDate(newDate);
    let calculatedStartDate;

    const calculateUpdatedStartDate = () => {
      calculatedStartDate = newDate;

      if (selectedOption === "Last 7 days" && newDate instanceof dayjs) {
        calculatedStartDate = newDate.subtract(6, "days");
      } else if (
        selectedOption === "Last 30 days" &&
        newDate instanceof dayjs
      ) {
        calculatedStartDate = newDate.subtract(30, "days");
      } else if (
        selectedOption === "Last 60 days" &&
        newDate instanceof dayjs
      ) {
        calculatedStartDate = newDate.subtract(60, "days");
      }
      setStartDate(calculatedStartDate);
    };
    calculateUpdatedStartDate();
  };

  const handleCallCenterChange = (
    newSelection: string,
    selectedCallCenter: string
  ) => {
    if (newSelection !== selectedCallCenter) {
      setSelectedCallCenter(newSelection);
      setClickedLabel(null);
      //setSelectedRadio('total_call_volume');
    }
  };

  // Get Flask data with proxy
  useEffect(() => {
    const selectedOptionInt = parseInt(selectedOption.replace(/\D/g, ""), 10);

    setNumbersAsInt(selectedOptionInt || 0);

    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    let formattedCallCenter;
    if (selectedCallCenter === "All") {
      formattedCallCenter = "";
    } else {
      formattedCallCenter = selectedCallCenter;
    }

    Promise.all([
      fetch(
        `/total_call_volume?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${formattedCallCenter}`
      ),
      fetch(
        `/average_handle_time?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${formattedCallCenter}`
      ),
      fetch(
        `/resolution_rate?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${formattedCallCenter}`
      ),
      fetch(
        `/average_wait_time?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center_id=${formattedCallCenter}`
      ),
    ])
      .then(([resTotalCall, resAveHandleTime, resResolRate, resAveWaitTime]) =>
        Promise.all([
          resTotalCall.json(),
          resAveHandleTime.json(),
          resResolRate.json(),
          resAveWaitTime.json(),
        ])
      )
      .then(
        ([
          dataTotalCall,
          dataAveHandleTime,
          dataResolRate,
          dataAveWaitTime,
        ]) => {
          setTotalCall(dataTotalCall);
          setAveHandleTime(dataAveHandleTime);
          setResolRate(dataResolRate);
          setAveWaittime(dataAveWaitTime);
        }
      );
  }, [selectedOption, selectedCallCenter, startDate, endDate]);

  useEffect(() => {
    setIsLoading(true);
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];
    const fetchData = async () => {
      try {
        const response = await fetch(
          `/llm_alerts?start_date=${formattedStartDate}&end_date=${formattedEndDate}`
        );
        const result: { key_alerts: { response: string }[] } =
          await response.json();
        //console.log(result)
        setAlertData(result);
        setAlertText([
          result.key_alerts[0].response,
          result.key_alerts[1].response,
        ]);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedOption, startDate, endDate]);

  useEffect(() => {
    setIsHighlightLoading(true);
    const formattedStartDate = startDate.toISOString().split("T")[0];
    const formattedEndDate = endDate.toISOString().split("T")[0];

    const fetchHighlightsData = async (url: string) => {
      try {
        const response = await fetch(url);
        const result = await response.json();
        setHighlightData(result);
        setHighlightText([
          result.key_highlights.response[0].replace(/^\d+\.\s/, ""),
          result.key_highlights.response[1].replace(/^\d+\.\s/, ""),
          result.key_highlights.response[2].replace(/^\d+\.\s/, ""),
        ]);
        setIsHighlightLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsHighlightLoading(false);
      }
    };

    if (selectedProductFromChild === "" && selectedCallCenter === "All") {
      fetchHighlightsData(
        `/llm_highlights?start_date=${formattedStartDate}&end_date=${formattedEndDate}`
      );
    } else if (
      selectedProductFromChild !== "" &&
      selectedCallCenter === "All"
    ) {
      const formattedProductName = selectedProductFromChild.replace(
        / /g,
        "%20"
      );
      const url = `/llm_highlights?start_date=${formattedStartDate}&end_date=${formattedEndDate}&products_name=${formattedProductName}`;
      fetchHighlightsData(url);
    } else if (
      selectedProductFromChild === "" &&
      selectedCallCenter !== "All"
    ) {
      const url = `/llm_highlights?start_date=${formattedStartDate}&end_date=${formattedEndDate}&call_center=${selectedCallCenter}`;
      fetchHighlightsData(url);
    } else if (
      selectedProductFromChild !== "" &&
      selectedCallCenter !== "All"
    ) {
      const formattedProductName = selectedProductFromChild.replace(
        / /g,
        "%20"
      );
      const url = `/llm_highlights?start_date=${formattedStartDate}&end_date=${formattedEndDate}&products_name=${formattedProductName}&call_center=${selectedCallCenter}`;
      fetchHighlightsData(url);
    }
  }, [
    selectedOption,
    startDate,
    endDate,
    selectedProductFromChild,
    selectedCallCenter,
  ]);

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRadio(e.target.value);
    setSelectedProductFromChild("");
    setSelectedReasonFromChild("");
    setIsBeingResetProduct(true);
    setIsBeingResetChart(true);
    setClickedLabel(null);
  };

  const handleSendReport = () => {
    //console.log('Sending report:', { email, comments });
    setIsModalOpen(false);
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const openImage = () => {
    setIsImageOpen(true);
  };

  const closeImage = () => {
    setIsImageOpen(false);
  };

  const handleReset = () => {
    setIsBeingResetProduct(true);
    setIsBeingResetChart(true);
    setSelectedRadio("resolution_rate");
    setTotalCall([]);
    setAveHandleTime([]);
    setResolRate([]);
    setAveWaittime([]);
    setNumbersAsInt(0);
    setSelectedCallCenter("All");
    setSelectedOption("Last 7 days");
    setEndDate(dayjs("2024-01-31"));
    setStartDate(dayjs("2024-01-24"));
    setAlertData([]);
    setAlertText([]);
    setIsLoading(true);
    setHighlightData([]);
    setIsHighlightLoading(true);
    setClickedLabel(null);
    setSelectedProductFromChild("");
    setSelectedReasonFromChild("");
  };

  const changeResetProduct = () => {
    setIsBeingResetProduct(false);
  };

  const changeResetChart = () => {
    setIsBeingResetChart(false);
  };

  return (
    <FadeIn transitionDuration={700}>
      <div className='exec-root container'>
        <FadeIn transitionDuration={700}>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <FadeIn transitionDuration={700}>
                  <Item className='exec-navbar-filters'>
                    <Box sx={{ flexGrow: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={12} md={2} lg={1.5} xl={1.5}>
                          <FormControl fullWidth>
                            <InputLabel id='select-label'>
                              Reference Period
                            </InputLabel>
                            <Select
                              labelId='select-label'
                              id='select'
                              value={selectedOption}
                              onChange={(e) => {
                                setSelectedOption(e.target.value);
                                handleEndDateChange(e.target.value, endDate);
                              }}
                            >
                              <MenuItem value='Last 7 days'>
                                Last 7 days
                              </MenuItem>
                              <MenuItem value='Last 30 days'>
                                Last 30 days
                              </MenuItem>
                              <MenuItem value='Last 60 days'>
                                Last 60 days
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={2} lg={1.5} xl={1.5}>
                          <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale='en'
                          >
                            <div>
                              <DatePicker
                                className='exec-date-picker'
                                defaultValue={dayjs("2024-01-31")}
                                label='Report Date'
                                value={endDate}
                                onChange={(e) => {
                                  handleEndDateChange(
                                    selectedOption,
                                    e || dayjs()
                                  );
                                }}
                              />
                            </div>
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={12} md={2} lg={1.5} xl={1.5}>
                          <FormControl fullWidth>
                            <InputLabel id='select-call-center'>
                              Call Center
                            </InputLabel>
                            <Select
                              labelId='select-cc-label'
                              id='select-cc'
                              value={selectedCallCenter}
                              onChange={(e) => {
                                setSelectedCallCenter(e.target.value);
                                handleCallCenterChange(
                                  e.target.value,
                                  selectedCallCenter
                                );
                              }}
                            >
                              <MenuItem value='All'>All</MenuItem>
                              <MenuItem value='CC001'>
                                Davis-Morgan Center
                              </MenuItem>
                              <MenuItem value='CC002'>
                                Dixon, Johnson and Dickerson Center
                              </MenuItem>
                              <MenuItem value='CC003'>
                                Davis-Ross Center
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                          <div className='exec-buttons'>
                            <Button
                              className='exec-nav-btn'
                              variant='contained'
                              endIcon={<RestartAltIcon />}
                              onClick={handleReset}
                            >
                              Reset Filters
                            </Button>
                            <Button
                              className='exec-nav-btn'
                              variant='contained'
                              endIcon={<SendIcon />}
                              onClick={() => {
                                onButtonClick();
                                openModal();
                              }}
                            >
                              Send Report
                            </Button>
                          </div>
                        </Grid>
                        <Modal
                          className='exec-send-modal modal-global'
                          open={isModalOpen}
                          onClose={closeModal}
                          aria-labelledby='modal-modal-title'
                          aria-describedby='modal-modal-description'
                        >
                          <Box className='modal-style' sx={ModalStyle}>
                            <h3>
                              <SendIcon /> Report Form
                            </h3>
                            <div>
                              <TextField
                                label='Subject'
                                variant='outlined'
                                fullWidth
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                margin='normal'
                              />
                              <TextField
                                label='Email'
                                variant='outlined'
                                fullWidth
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                margin='normal'
                              />
                              <TextField
                                label='Additional Comments'
                                variant='outlined'
                                fullWidth
                                multiline
                                rows={4}
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                margin='normal'
                              />
                              <FormControl
                                fullWidth
                                className='send-report-dropdown'
                              >
                                <InputLabel id='select-label'>
                                  Report Frequency
                                </InputLabel>
                                <Select
                                  labelId='select-label'
                                  id='select'
                                  value={frequency}
                                  onChange={(e) => {
                                    setFrequency(e.target.value);
                                  }}
                                >
                                  <MenuItem value='Daily'>Daily</MenuItem>
                                  <MenuItem value='Weekly'>Weekly</MenuItem>
                                  <MenuItem value='Monthly'>Monthly</MenuItem>
                                </Select>
                              </FormControl>
                              <TextField
                                label='Num of Recurrences'
                                variant='outlined'
                                fullWidth
                                type='number'
                                value={recurrences}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value);
                                  if (
                                    !isNaN(value) &&
                                    value >= 0 &&
                                    value <= 365
                                  ) {
                                    setRecurrences(value.toString());
                                  } else if (e.target.value === "") {
                                    setRecurrences("");
                                  }
                                }}
                                margin='normal'
                                inputProps={{ inputMode: "numeric", max: 365 }}
                              />
                              <p className='text-dark-mode'>
                                Recurrence Range:
                              </p>
                              <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                adapterLocale='en'
                              >
                                <div className='send-report-datepickers'>
                                  <DatePicker
                                    className='exec-date-picker'
                                    label='Start'
                                    value={rangeStartDate}
                                    onChange={(date) => {
                                      if (date && date.isBefore(Today, "day")) {
                                        setRangeStartDate(Today);
                                      } else {
                                        setRangeStartDate(date || Today);
                                      }
                                    }}
                                  />
                                  <span className='text-dark-mode'> to </span>
                                  <DatePicker
                                    className='exec-date-picker'
                                    label='End'
                                    value={rangeEndDate}
                                    onChange={(date) => {
                                      if (date && date.isBefore(Today, "day")) {
                                        setRangeEndDate(Today);
                                      } else if (
                                        rangeStartDate &&
                                        date &&
                                        date.isBefore(rangeStartDate, "day")
                                      ) {
                                        setRangeEndDate(rangeStartDate);
                                      } else {
                                        setRangeEndDate(date || Today);
                                      }
                                    }}
                                  />
                                </div>
                              </LocalizationProvider>
                              {screenshot && (
                                <div className='send-report-attachment'>
                                  <p>Attachment:</p>
                                  <img
                                    src={screenshot}
                                    alt='Screenshot'
                                    onClick={openImage}
                                  />
                                </div>
                              )}
                            </div>
                            <div>
                              <Button
                                className='exec-send-btn'
                                variant='contained'
                                onClick={handleSendReport}
                              >
                                Send
                              </Button>
                            </div>
                          </Box>
                        </Modal>
                        <Modal
                          className='exec-send-modal modal-global'
                          open={isImageOpen}
                          onClose={closeImage}
                          aria-labelledby='modal-modal-title'
                          aria-describedby='modal-modal-description'
                        >
                          <Box sx={ImageStyle}>
                            <div className='magnified-content'>
                              <img
                                src={screenshot}
                                alt='Magnified Screenshot'
                              />
                            </div>
                          </Box>
                        </Modal>
                      </Grid>
                    </Box>
                  </Item>
                </FadeIn>
              </Grid>
              <Grid item xs={12} className='exec-alert-wrapper'>
                <Item className='exec-navbar'>
                  <Box sx={{ flexGrow: 1 }}>
                    <Grid container spacing={2}>
                      {isLoading ||
                      alertData.llm_response === "" ||
                      alertData.length === 0 ? (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                          xl={6}
                          className='exec-alert'
                        >
                          <Skeleton height={70} />
                          <span className='centered-text'>
                            <GridLoading
                              visible={true}
                              height='15'
                              width='15'
                              color={mode === "dark" ? "white" : "black"}
                              ariaLabel='grid-loading'
                              radius='12.5'
                              wrapperStyle={{}}
                              wrapperClass='grid-wrapper'
                            />
                            AI is analyzing key alerts...
                          </span>
                        </Grid>
                      ) : (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                          xl={6}
                          className={`exec-alert ${
                            clickedLabel === 0 ? "clicked" : "not-clicked"
                          }`}
                          onClick={() => handleLabelClick(0)}
                        >
                          <div
                            className='alert alert--info'
                            data-tooltip-id='exec-tooltip'
                          >
                            {mode === "dark" ? (
                              <AutoAwesomeIcon />
                            ) : (
                              <span className='ai-driven-light'>
                                <AutoAwesomeIcon />
                              </span>
                            )}
                            <div className='content'>
                              <div className='body'>
                                {/* <span>{alertText[0]}</span> */}
                                <TypeAnimation
                                  sequence={[`${alertText[0]}`]}
                                  speed={{
                                    type: "keyStrokeDelayInMs",
                                    value: 10,
                                  }}
                                  cursor={false}
                                />
                              </div>
                            </div>
                          </div>
                        </Grid>
                      )}
                      {isLoading ||
                      alertData.llm_response === "" ||
                      alertData.length === 0 ? (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                          xl={6}
                          className='exec-alert'
                        >
                          <Skeleton height={70} />
                          <span className='centered-text'>
                            <GridLoading
                              visible={true}
                              height='15'
                              width='15'
                              color={mode === "dark" ? "white" : "black"}
                              ariaLabel='grid-loading'
                              radius='12.5'
                              wrapperStyle={{}}
                              wrapperClass='grid-wrapper'
                            />
                            AI is analyzing key alerts...
                          </span>
                        </Grid>
                      ) : (
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={12}
                          lg={6}
                          xl={6}
                          className={`exec-alert ${
                            clickedLabel === 1 ? "clicked" : "not-clicked"
                          }`}
                          onClick={() => handleLabelClick(1)}
                        >
                          <div
                            className='alert alert--info'
                            data-tooltip-id='exec-tooltip'
                          >
                            {mode === "dark" ? (
                              <AutoAwesomeIcon />
                            ) : (
                              <span className='ai-driven-light'>
                                <AutoAwesomeIcon />
                              </span>
                            )}
                            <div className='content'>
                              <div className='body'>
                                {/* <span>{alertText[1]}</span> */}
                                <TypeAnimation
                                  sequence={[`${alertText[1]}`]}
                                  speed={{
                                    type: "keyStrokeDelayInMs",
                                    value: 10,
                                  }}
                                  cursor={false}
                                />
                              </div>
                            </div>
                          </div>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Item>
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={2.5} xl={2}>
                <Item className='exec-card'>
                  <FadeIn transitionDuration={700}>
                    <FormControl className='exec-summary-group-wrapper'>
                      <RadioGroup
                        className='exec-summary-group'
                        aria-labelledby='demo-radio-buttons-group-label'
                        defaultValue='total_call_volume'
                        name='radio-buttons-group'
                        value={selectedRadio ?? " "}
                        onChange={handleRadioChange}
                      >
                        <div className='radio-value-responsive'>
                          <FormControlLabel
                            value='resolution_rate'
                            control={<Radio />}
                            label='Resolution Rate​'
                          />
                          {typeof resolRateData.resolution_rate ===
                          "undefined" ? (
                            <Skeleton width='100%' height='100px' />
                          ) : (
                            <div className='exec-radio-values'>
                              <div className='exec-sidebar-number'>
                                {
                                  <SlotCounter
                                    value={parsePercentageString(
                                      resolRateData.resolution_rate
                                    )}
                                  />
                                }
                                <span className='percentage-sign'>%</span>
                                {mode === "dark" ? (
                                  <span className='shine'>
                                    <AutoAwesomeIcon />
                                  </span>
                                ) : (
                                  <span className='ai-driven'>
                                    <AutoAwesomeIcon />
                                  </span>
                                )}
                              </div>
                              {numbersAsInt !== 0 ? (
                                <>
                                  {selectedOption !== "Last 7 days" ||
                                  resolRateData.percentage_change === "N/A" ? (
                                    <span className='bold-num'>DATA N/A</span>
                                  ) : (
                                    <span className={`exec-sidebar-percentage`}>
                                      <span
                                        className={`${
                                          parseFloat(
                                            resolRateData.percentage_change
                                          ) === 0
                                            ? "no-arrow"
                                            : parseFloat(
                                                resolRateData.percentage_change
                                              ) < 0
                                            ? "red-arrow"
                                            : "green-arrow"
                                        } number`}
                                      >
                                        {parseFloat(
                                          resolRateData.percentage_change
                                        ) === 0
                                          ? "00.0%"
                                          : (parseFloat(
                                              resolRateData.percentage_change
                                            ) < 0
                                              ? ""
                                              : "") +
                                            Math.abs(
                                              parseFloat(
                                                resolRateData.percentage_change
                                              )
                                            ).toFixed(1) +
                                            "%"}
                                        {parseFloat(
                                          resolRateData.percentage_change
                                        ) === 0
                                          ? "⬅"
                                          : parseFloat(
                                              resolRateData.percentage_change
                                            ) < 0
                                          ? "⬇"
                                          : "⬆"}
                                      </span>
                                    </span>
                                  )}
                                  <br />
                                  <span className='exec-sidebar-comment'>
                                    vs. previous {numbersAsInt} days
                                  </span>
                                </>
                              ) : (
                                <>
                                  {selectedOption !== "Last 7 days" ||
                                  resolRateData.percentage_change === "N/A" ? (
                                    <span className='bold-num'>DATA N/A</span>
                                  ) : (
                                    <span className={`exec-sidebar-percentage`}>
                                      <span
                                        className={`${
                                          parseFloat(
                                            resolRateData.percentage_change
                                          ) === 0
                                            ? "no-arrow"
                                            : parseFloat(
                                                resolRateData.percentage_change
                                              ) < 0
                                            ? "red-arrow"
                                            : "green-arrow"
                                        } number`}
                                      >
                                        {parseFloat(
                                          resolRateData.percentage_change
                                        ) === 0
                                          ? "00.0%"
                                          : (parseFloat(
                                              resolRateData.percentage_change
                                            ) < 0
                                              ? ""
                                              : "") +
                                            Math.abs(
                                              parseFloat(
                                                resolRateData.percentage_change
                                              )
                                            ).toFixed(1) +
                                            "%"}
                                        {parseFloat(
                                          resolRateData.percentage_change
                                        ) === 0
                                          ? "⬅"
                                          : parseFloat(
                                              resolRateData.percentage_change
                                            ) < 0
                                          ? "⬇"
                                          : "⬆"}
                                      </span>
                                    </span>
                                  )}
                                  <br />
                                  <span className='exec-sidebar-comment'>
                                    vs. previous {numbersAsInt} days
                                  </span>
                                </>
                              )}
                              <Divider className='divider-1' />
                            </div>
                          )}
                        </div>
                        <div className='radio-value-responsive'>
                          <FormControlLabel
                            value='total_call_volume'
                            control={<Radio />}
                            label='Total Call Volume'
                          />
                          {typeof totalCallData.total_call_volume ===
                          "undefined" ? (
                            <Skeleton width='100%' height='100px' />
                          ) : (
                            <div className='exec-radio-values'>
                              <div className='exec-sidebar-number'>
                                {
                                  <SlotCounter
                                    value={totalCallData.total_call_volume}
                                  />
                                }
                              </div>
                              {numbersAsInt !== 0 ? (
                                <>
                                  {selectedOption !== "Last 7 days" ||
                                  totalCallData.percentage_change === "N/A" ? (
                                    <span className='bold-num'>DATA N/A</span>
                                  ) : (
                                    <span className={`exec-sidebar-percentage`}>
                                      <span
                                        className={`${
                                          parseFloat(
                                            totalCallData.percentage_change
                                          ) === 0
                                            ? "no-arrow"
                                            : parseFloat(
                                                totalCallData.percentage_change
                                              ) > 0
                                            ? "red-arrow"
                                            : "green-arrow"
                                        } number`}
                                      >
                                        {parseFloat(
                                          totalCallData.percentage_change
                                        ) === 0
                                          ? "00.0%"
                                          : (parseFloat(
                                              totalCallData.percentage_change
                                            ) < 0
                                              ? ""
                                              : "") +
                                            Math.abs(
                                              parseFloat(
                                                totalCallData.percentage_change
                                              )
                                            ).toFixed(1) +
                                            "%"}
                                        {parseFloat(
                                          totalCallData.percentage_change
                                        ) === 0
                                          ? "⬅"
                                          : parseFloat(
                                              totalCallData.percentage_change
                                            ) < 0
                                          ? "⬇"
                                          : "⬆"}
                                      </span>
                                    </span>
                                  )}
                                  <br />
                                  <span className='exec-sidebar-comment'>
                                    vs. previous {numbersAsInt} days
                                  </span>
                                </>
                              ) : (
                                <>
                                  {selectedOption !== "Last 7 days" ||
                                  totalCallData.percentage_change === "N/A" ? (
                                    <span className='bold-num'>DATA N/A</span>
                                  ) : (
                                    <span className={`exec-sidebar-percentage`}>
                                      <span
                                        className={`${
                                          parseFloat(
                                            totalCallData.percentage_change
                                          ) === 0
                                            ? "no-arrow"
                                            : parseFloat(
                                                totalCallData.percentage_change
                                              ) < 0
                                            ? "red-arrow"
                                            : "green-arrow"
                                        } number`}
                                      >
                                        {parseFloat(
                                          totalCallData.percentage_change
                                        ) === 0
                                          ? "00.0%"
                                          : (parseFloat(
                                              totalCallData.percentage_change
                                            ) < 0
                                              ? ""
                                              : "") +
                                            Math.abs(
                                              parseFloat(
                                                totalCallData.percentage_change
                                              )
                                            ).toFixed(1) +
                                            "%"}
                                        {parseFloat(
                                          totalCallData.percentage_change
                                        ) === 0
                                          ? "⬅"
                                          : parseFloat(
                                              totalCallData.percentage_change
                                            ) < 0
                                          ? "⬇"
                                          : "⬆"}
                                      </span>
                                    </span>
                                  )}
                                  <br />
                                  <span className='exec-sidebar-comment'>
                                    vs. previous day
                                  </span>
                                </>
                              )}
                              <Divider className='divider-1' />
                            </div>
                          )}
                        </div>
                        <div className='radio-value-responsive'>
                          <FormControlLabel
                            value='average_wait_time'
                            control={<Radio />}
                            label='Customer Satisfaction'
                          />
                          {typeof aveWaitData.average_wait_time ===
                          "undefined" ? (
                            <Skeleton width='100%' height='100px' />
                          ) : (
                            <div className='exec-radio-values'>
                              <div className='exec-sidebar-number'>
                                {<SlotCounter value={3.4} />}
                                <span className='percentage-sign'>/5</span>
                              </div>
                              {numbersAsInt !== 0 ? (
                                <>
                                  {selectedOption !== "Last 7 days" ||
                                  aveWaitData.percentage_change === "N/A" ? (
                                    <span className='bold-num'>DATA N/A</span>
                                  ) : (
                                    <span className={`exec-sidebar-percentage`}>
                                      <span className={`green-arrow number`}>
                                        0.2% ⬆
                                      </span>
                                    </span>
                                  )}
                                  <br />
                                  <span className='exec-sidebar-comment'>
                                    vs. previous {numbersAsInt} days
                                  </span>
                                </>
                              ) : (
                                <>
                                  {selectedOption !== "Last 7 days" ||
                                  aveWaitData.percentage_change === "N/A" ? (
                                    <span className='bold-num'>DATA N/A</span>
                                  ) : (
                                    <span className={`exec-sidebar-percentage`}>
                                      <span
                                        className={`${
                                          parseFloat(
                                            aveWaitData.percentage_change
                                          ) === 0
                                            ? "no-arrow"
                                            : parseFloat(
                                                aveWaitData.percentage_change
                                              ) > 0
                                            ? "red-arrow"
                                            : "green-arrow"
                                        } number`}
                                      >
                                        {parseFloat(
                                          aveWaitData.percentage_change
                                        ) === 0
                                          ? "00.0%"
                                          : (parseFloat(
                                              aveWaitData.percentage_change
                                            ) < 0
                                              ? ""
                                              : "") +
                                            Math.abs(
                                              parseFloat(
                                                aveWaitData.percentage_change
                                              )
                                            ).toFixed(1) +
                                            "%"}
                                        {parseFloat(
                                          aveWaitData.percentage_change
                                        ) === 0
                                          ? "⬅"
                                          : parseFloat(
                                              aveWaitData.percentage_change
                                            ) < 0
                                          ? "⬇"
                                          : "⬆"}
                                      </span>
                                    </span>
                                  )}
                                  <br />
                                  <span className='exec-sidebar-comment'>
                                    vs. previous day
                                  </span>
                                </>
                              )}
                              <Divider className='divider-1' />
                            </div>
                          )}
                        </div>
                        {/* <div className='radio-value-responsive'>
                                    <FormControlLabel value="average_wait_time" control={<Radio />} label="Average Wait Time" />
                                    {(typeof aveWaitData.average_wait_time === 'undefined') ? (
                                        <Skeleton width="100%" height="100px"/>
                                    ):  <div className="exec-radio-values">
                                            <div className="exec-sidebar-number">{ <SlotCounter value={Math.round(aveWaitData.average_wait_time)}/>}<span className='percentage-sign'>sec</span></div>
                                            {   numbersAsInt !== 0 ? (
                                            <>
                                            {selectedOption !== 'Last 7 days' || aveWaitData.percentage_change === 'N/A' ? (
                                                <span className='bold-num'>DATA N/A</span>
                                            ) : (
                                                <span className={`exec-sidebar-percentage`}>
                                                    <span className={`${parseFloat(aveWaitData.percentage_change) === 0 ? 'no-arrow' : (parseFloat(aveWaitData.percentage_change) > 0 ? 'red-arrow' : 'green-arrow')} number`}>
                                                    {parseFloat(aveWaitData.percentage_change) === 0 ? '00.0%' : (parseFloat(aveWaitData.percentage_change) < 0 ? '' : '') + Math.abs(parseFloat(aveWaitData.percentage_change)).toFixed(1) + '%'}
                                                    {parseFloat(aveWaitData.percentage_change) === 0 ? '⬅' : (parseFloat(aveWaitData.percentage_change) < 0 ? '⬇' : '⬆')}
                                                    </span>
                                                </span>
                                            )}
                                                <br/>
                                                <span className='exec-sidebar-comment'>vs. previous {numbersAsInt} days</span>
                                            </>
                                            ) : (
                                            <>
                                            {selectedOption !== 'Last 7 days' || aveWaitData.percentage_change === 'N/A' ? (
                                                <span className='bold-num'>DATA N/A</span>
                                            ) : (
                                                <span className={`exec-sidebar-percentage`}>
                                                    <span className={`${parseFloat(aveWaitData.percentage_change) === 0 ? 'no-arrow' : (parseFloat(aveWaitData.percentage_change) > 0 ? 'red-arrow' : 'green-arrow')} number`}>
                                                    {parseFloat(aveWaitData.percentage_change) === 0 ? '00.0%' : (parseFloat(aveWaitData.percentage_change) < 0 ? '' : '') + Math.abs(parseFloat(aveWaitData.percentage_change)).toFixed(1) + '%'}
                                                    {parseFloat(aveWaitData.percentage_change) === 0 ? '⬅' : (parseFloat(aveWaitData.percentage_change) < 0 ? '⬇' : '⬆')}
                                                    </span>
                                                </span>
                                            )}
                                                <br/>
                                                <span className='exec-sidebar-comment'>vs. previous day</span>
                                            </>
                                            )}
                                            <Divider className='divider-1' />
                                        </div>
                                    }
                                </div> */}
                        <div className='radio-value-responsive'>
                          <FormControlLabel
                            value='average_handle_time'
                            control={<Radio />}
                            label='Average Handle Time​'
                          />
                          {typeof aveHandleData.average_handle_time ===
                          "undefined" ? (
                            <Skeleton width='100%' height='100px' />
                          ) : (
                            <div className='exec-radio-values'>
                              <div className='exec-sidebar-number'>
                                {
                                  <SlotCounter
                                    value={Math.round(
                                      aveHandleData.average_handle_time
                                    )}
                                  />
                                }
                                <span className='percentage-sign'>min</span>
                              </div>
                              {numbersAsInt !== 0 ? (
                                <>
                                  {selectedOption !== "Last 7 days" ||
                                  aveHandleData.percentage_change === "N/A" ? (
                                    <span className='bold-num'>DATA N/A</span>
                                  ) : (
                                    <span className={`exec-sidebar-percentage`}>
                                      <span
                                        className={`${
                                          parseFloat(
                                            aveHandleData.percentage_change
                                          ) === 0 ||
                                          Math.abs(
                                            parseFloat(
                                              aveHandleData.percentage_change
                                            )
                                          ).toFixed(1) === "0.0"
                                            ? "no-arrow"
                                            : parseFloat(
                                                aveHandleData.percentage_change
                                              ) > 0
                                            ? "red-arrow"
                                            : "green-arrow"
                                        } number`}
                                      >
                                        {parseFloat(
                                          aveHandleData.percentage_change
                                        ) === 0
                                          ? "00.0%"
                                          : (parseFloat(
                                              aveHandleData.percentage_change
                                            ) < 0
                                              ? ""
                                              : "") +
                                            Math.abs(
                                              parseFloat(
                                                aveHandleData.percentage_change
                                              )
                                            ).toFixed(1) +
                                            "%"}
                                        {parseFloat(
                                          aveHandleData.percentage_change
                                        ) === 0 ||
                                        Math.abs(
                                          parseFloat(
                                            aveHandleData.percentage_change
                                          )
                                        ).toFixed(1) === "0.0"
                                          ? "⬅"
                                          : parseFloat(
                                              aveHandleData.percentage_change
                                            ) < 0
                                          ? "⬇"
                                          : "⬆"}
                                      </span>
                                    </span>
                                  )}
                                  <br />
                                  <span className='exec-sidebar-comment'>
                                    vs. previous {numbersAsInt} days
                                  </span>
                                </>
                              ) : (
                                <>
                                  {selectedOption !== "Last 7 days" ||
                                  aveHandleData.percentage_change === "N/A" ? (
                                    <span className='bold-num'>DATA N/A</span>
                                  ) : (
                                    <span className={`exec-sidebar-percentage`}>
                                      <span
                                        className={`${
                                          parseFloat(
                                            aveHandleData.percentage_change
                                          ) === 0
                                            ? "no-arrow"
                                            : parseFloat(
                                                aveHandleData.percentage_change
                                              ) > 0
                                            ? "red-arrow"
                                            : "green-arrow"
                                        } number`}
                                      >
                                        {parseFloat(
                                          aveHandleData.percentage_change
                                        ) === 0
                                          ? "00.0%"
                                          : (parseFloat(
                                              aveHandleData.percentage_change
                                            ) < 0
                                              ? ""
                                              : "") +
                                            Math.abs(
                                              parseFloat(
                                                aveHandleData.percentage_change
                                              )
                                            ).toFixed(1) +
                                            "%"}
                                        {parseFloat(
                                          aveHandleData.percentage_change
                                        ) === 0
                                          ? "⬅"
                                          : parseFloat(
                                              aveHandleData.percentage_change
                                            ) < 0
                                          ? "⬇"
                                          : "⬆"}
                                      </span>
                                    </span>
                                  )}
                                  <br />
                                  <span className='exec-sidebar-comment'>
                                    vs. previous day
                                  </span>
                                </>
                              )}
                              <Divider className='divider-1' />
                            </div>
                          )}
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FadeIn>
                </Item>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
                <Item className='exec-card'>
                  <FadeIn transitionDuration={700}>
                    <React.StrictMode>
                      <ExecChart
                        parentToChild={{
                          startDate,
                          endDate,
                          selectedOption,
                          selectedRadio,
                          isBeingResetChart,
                          selectedProductFromChild,
                          selectedReasonFromChild,
                          selectedCallCenter,
                          mode,
                        }}
                        onResetChart={changeResetChart}
                      />
                      <Product
                        parentToChild={{
                          startDate,
                          endDate,
                          selectedOption,
                          selectedRadio,
                          isBeingResetProduct,
                          selectedCallCenter,
                          mode,
                        }}
                        onResetProduct={changeResetProduct}
                        onSelectedProductChange={handleSelectedProductChange}
                        onSelectedReasonChange={handleSelectedReasonChange}
                      />
                    </React.StrictMode>
                  </FadeIn>
                </Item>
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={5.5} xl={6}>
                <Item className='exec-card exec-search-section'>
                  <FadeIn transitionDuration={700}>
                    {isHighlightLoading ||
                    highlightData === undefined ||
                    highlightData.length === 0 ? (
                      <div className='position-reletive'>
                        <Skeleton height={150} />
                        <span className='centered-text'>
                          <GridLoading
                            visible={true}
                            height='15'
                            width='15'
                            color={mode === "dark" ? "white" : "black"}
                            ariaLabel='grid-loading'
                            radius='12.5'
                            wrapperStyle={{}}
                            wrapperClass='grid-wrapper'
                          />
                          AI is generating key highlights...
                        </span>
                      </div>
                    ) : (
                      <div className='exec-highlight'>
                        <h3>
                          Key Highlights
                          {mode === "dark" ? (
                            <span className='shine'>
                              <AutoAwesomeIcon />
                            </span>
                          ) : (
                            <span className='ai-driven'>
                              <AutoAwesomeIcon />
                            </span>
                          )}
                        </h3>
                        <ul>
                          <li className='flex-list'>
                            <ArrowRightIcon />
                            <TypeAnimation
                              sequence={[`${highlightText[0]}`]}
                              speed={{ type: "keyStrokeDelayInMs", value: 10 }}
                              cursor={false}
                            />
                          </li>
                          <li className='flex-list'>
                            <ArrowRightIcon />
                            <TypeAnimation
                              sequence={[`${highlightText[1]}`]}
                              speed={{ type: "keyStrokeDelayInMs", value: 10 }}
                              cursor={false}
                            />
                          </li>
                          <li className='flex-list'>
                            <ArrowRightIcon />
                            <TypeAnimation
                              sequence={[`${highlightText[2]}`]}
                              speed={{ type: "keyStrokeDelayInMs", value: 10 }}
                              cursor={false}
                            />
                          </li>
                        </ul>
                      </div>
                    )}
                    <React.StrictMode>
                      <NewChatInterface parentToChild={{ mode }} />
                    </React.StrictMode>
                  </FadeIn>
                </Item>
              </Grid>
            </Grid>
          </Box>
        </FadeIn>
      </div>
      <ReactTooltip
        id='exec-tooltip'
        style={{
          borderRadius: "5px",
          ...(mode === "dark" && {
            backgroundColor: "#f5f5f5",
            color: "black",
          }),
        }}
        place='bottom'
        content='Select to filter dashboard'
      />
    </FadeIn>
  );
}

export default Executive;
